import { useState } from 'react'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from '@10xLabs/react-web/components/Row'
import { Select } from 'components/v2/Select'
import FormLayout from 'features/App/components/FormLayout'
import { BulkItemsForm, EMPTY_ITEM } from 'features/Inventories/components/BulkItemsForm'
import { BulkItemsSummary } from 'features/Inventories/components/BulkItemsSummary'
import { InputSupplierSuggestion } from 'features/Inventories/components/InputSupplierSuggestion'
import { InventoryAutocomplete, getInventoryLabel } from 'features/Inventories/components/InventoryAutocomplete'
import { ORDER_TYPE } from 'features/Inventories/components/OrderType/constants'
import { LOST_TYPE } from 'features/Snacks/components/LostType/constants'
import { lostTypeMessages } from 'features/Snacks/components/LostType/messages'
import { REMOVE_ITEMS_TYPE } from 'features/Snacks/components/RemoveItemsType/constants'
import { removeItemsTypeMessages } from 'features/Snacks/components/RemoveItemsType/messages'
import { getDestinationInventoryType } from 'resolvers/inventories'
import { schema } from './schema'

const REMOVE_ITEMS_TYPE_LIST = [REMOVE_ITEMS_TYPE.WASTE, ORDER_TYPE.SHIPMENT]

const LOST_REASON_LIST = [LOST_TYPE.ADMINISTRATIVE_ERROR, LOST_TYPE.OPERATIVE_WASTE, LOST_TYPE.EXPIRED]

const messages = defineMessages({
  purchaseIdPlaceholder: {
    id: 'spanish.items.purchaseId',
    defaultMessage: 'Compra id'
  },
  supplierPlaceholder: {
    id: 'spanish.items.supplier',
    defaultMessage: 'Proveedor'
  },
  selectType: {
    id: 'spanish..inventories.items.remove.selectType',
    defaultMessage: 'Seleccionar tipo'
  },
  selectReason: {
    id: 'spanish.inventories.items.remove.selectReason',
    defaultMessage: 'Seleccionar razón'
  },
  destinationPlaceholder: {
    id: 'spanish.inventories.items.remove.destinationPlaceholder',
    defaultMessage: 'Seleccionar destino'
  }
})

const RemoveBulkItemsForm = (props: TSFixMe): TSFixMe => {
  const { companyId, inventory, values, handleSubmit, handleChange, handleBlur, setFieldValue, errors, touched, urls } =
    props

  const intl = useIntl()

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.inventories.removeBulkItems.form.general.title" defaultMessage="General" />
            </Heading.H5>
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <Select
              id="type"
              value={values.type}
              onChange={handleChange}
              touched={touched.type}
              errors={errors.type}
              label={<FormattedMessage id="spanish.dictionary.type" defaultMessage="Tipo" />}
            >
              <option value="">{intl.formatMessage(messages.selectType)}</option>

              {REMOVE_ITEMS_TYPE_LIST.map((type): TSFixMe => {
                return (
                  <option value={type} key={type}>
                    {removeItemsTypeMessages[type] && intl.formatMessage(removeItemsTypeMessages[type])}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>

        {values.type === REMOVE_ITEMS_TYPE.WASTE && (
          <Row>
            <Column width="50%">
              <Select
                id="reason"
                value={values.reason}
                onChange={handleChange}
                touched={touched.reason}
                errors={errors.reason}
                label={<FormattedMessage id="spanish.dictionary.reason" defaultMessage="Razón" />}
              >
                <option value="">{intl.formatMessage(messages.selectReason)}</option>

                {LOST_REASON_LIST.map((reason): TSFixMe => {
                  return (
                    <option value={reason} key={reason}>
                      {lostTypeMessages[reason] && intl.formatMessage(lostTypeMessages[reason])}
                    </option>
                  )
                })}
              </Select>
            </Column>
          </Row>
        )}

        {values.type === REMOVE_ITEMS_TYPE.WASTE && values.reason === LOST_TYPE.ADMINISTRATIVE_ERROR && (
          <>
            <Row>
              <Column width="50%">
                <Input
                  id="purchaseId"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.purchaseId}
                  placeholder={intl.formatMessage(messages.purchaseIdPlaceholder)}
                  touched={touched.purchaseId}
                  errors={errors.purchaseId}
                  label={<FormattedMessage id="spanish.items.purchaseId" defaultMessage="Compra id" />}
                />
              </Column>
            </Row>

            <Row>
              <Column width="50%">
                <InputSupplierSuggestion
                  id="supplier"
                  onChange={(supplier: TSFixMe): TSFixMe => setFieldValue('supplier', supplier)}
                  onBlur={handleBlur}
                  value={values.supplier}
                  companyId={companyId}
                  placeholder={intl.formatMessage(messages.supplierPlaceholder)}
                  label={<FormattedMessage id="spanish.dictionary.supplier" defaultMessage="Proveedor" />}
                  touched={touched.supplier}
                  errors={errors.supplier}
                />
              </Column>
            </Row>
          </>
        )}

        {values.type === ORDER_TYPE.SHIPMENT && (
          <Row>
            <Column width="50%">
              <InventoryAutocomplete
                placeholder={intl.formatMessage(messages.destinationPlaceholder)}
                value={getInventoryLabel(values.destination, intl)}
                label={<FormattedMessage id="spanish.dictionary.destination" defaultMessage="Destino" />}
                onChange={(inventory: TSFixMe): TSFixMe => {
                  setFieldValue('destination', inventory)
                }}
                excludedInventories={[inventory.id]}
                types={getDestinationInventoryType(inventory)}
                companyId={companyId}
                touched={touched.destination && touched.destination.id}
                errors={errors.destination && errors.destination.id}
              />
            </Column>
          </Row>
        )}

        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.inventories.removeBulkItems.form.items.title" defaultMessage="Productos" />
            </Heading.H5>
          </Column>
        </Row>

        <BulkItemsForm {...props} noPrice={values.type !== REMOVE_ITEMS_TYPE.WASTE} />
      </FormLayout.CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        <FormLayout.CardFooterActions>
          <Button
            as={NavLink}
            to={urls.cancelOrder ? urls.cancelOrder : `/inventories/${inventory.id}/outbounds`}
            kind="cancel"
          >
            <FormattedMessage id="spanish.dictionary.cancel" defaultMessage="Cancelar" />
          </Button>

          <Button type="submit" kind="primary">
            <FormattedMessage id="spanish.inventories.removeBulkItems.form.next" defaultMessage="Ver resumen" />
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const RemoveBulkItems = ({ inventory = {}, onSubmit, error, loading, urls = {} }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialValues = {
    type: '',
    reason: '',
    destination: { id: '', name: '' },
    purchaseId: '',
    supplier: '',
    items: [EMPTY_ITEM]
  }

  const [step, setStep] = useState(1)

  const handleSubmit = (values: TSFixMe): TSFixMe => {
    if (step === 1) {
      setStep(2)
    } else {
      onSubmit(values)
    }
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit}>
      {(props): TSFixMe => {
        switch (step) {
          case 1:
            return (
              <RemoveBulkItemsForm
                {...props}
                companyId={inventory.company && inventory.company.id}
                inventory={inventory}
                urls={urls}
              />
            )
          case 2:
            return <BulkItemsSummary {...props} error={error} loading={loading} onCancel={(): TSFixMe => setStep(1)} />
        }
      }}
    </Formik>
  )
}
